import React, { useEffect, useState, useMemo } from 'react';
import { fetchPowerOutageForecast, fetchGroundwaterForecast, fetchInformalWorkersForecast, fetchConstructionLaborForecast } from '../../services/api';
import { getWardPropertyName } from '../../utils/mapUtils';
import { calculateGroundwaterSeverityLevel, calculateSeverityLevel, calculateInformalWorkersSeverityLevel, calculateLostLaborHoursSeverityLevel, calculateNoShadeWaterSeverityLevel, calculateHeatIllnessSeverityLevel } from '../../utils/severityUtils';
import { useDashboard } from '../../context/DashboardContext';

// Legend order: HIGH (red), MEDIUM (orange), LOW (green), N/A (grey) — same as map
const SEVERITY_ORDER = { HIGH: 3, MEDIUM: 2, LOW: 1, 'N/A': 0 };
const SEVERITY_COLORS = {
  HIGH: { bg: 'rgba(183, 28, 28, 0.12)', border: '#B71C1C' },
  MEDIUM: { bg: 'rgba(249, 168, 37, 0.2)', border: '#F9A825' },
  LOW: { bg: 'rgba(46, 125, 50, 0.15)', border: '#2E7D32' },
  'N/A': { bg: 'rgba(128, 128, 128, 0.1)', border: '#808080' }
};

const ForecastTableContainer = ({ city, forecastDay, geoJSON, isGroundwater = false, isInformalWorkers = false, isConstructionLabor = false, indicatorName = null }) => {
  const { getIndicatorThreshold, setSelectedWard } = useDashboard();
  const [forecastData, setForecastData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState(
    { key: 'severity_level', direction: 'desc' }
  );

  const filteredForecastData = useMemo(() => {
    if (!forecastData) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return forecastData;
    return forecastData.filter((row) => {
      const name = String(row.ward_name || '').toLowerCase();
      const num = String(row.ward_number ?? '');
      return name.includes(q) || num.includes(q);
    });
  }, [forecastData, searchQuery]);

  const wardsWithForecastData = useMemo(() => {
    if (!forecastData) return 0;
    if (isGroundwater) {
      return forecastData.filter(
        (r) =>
          r.predicted_depletion_level_meters != null ||
          r.predicted_current_gwl_meters != null ||
          r.predicted_mean_gwl_meters != null
      ).length;
    }
    // Power outage, informal workers, construction labor: count wards with actual forecast data (predicted_severity_index is not null)
    return forecastData.filter(
      (r) => r.predicted_severity_index != null
    ).length;
  }, [forecastData, isGroundwater]);

  const wardInfoByNumber = useMemo(() => {
    if (!geoJSON || !geoJSON.features) return {};
    const wardPropertyName = getWardPropertyName(geoJSON);
    const map = {};
    geoJSON.features.forEach((feature) => {
      const id = feature.properties?.[wardPropertyName];
      const num = typeof id === 'number' ? id : parseInt(id, 10);
      if (Number.isNaN(num)) return;
      const wardName =
        feature.properties.ward_name ||
        feature.properties.Ward_Name ||
        feature.properties.WARD_NAME ||
        `Ward ${num}`;
      map[num] = {
        id,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry
      };
    });
    return map;
  }, [geoJSON]);

  // Create a map of ward numbers to ward names from geoJSON
  const wardNameMap = useMemo(() => {
    if (!geoJSON || !geoJSON.features) return {};
    
    const wardPropertyName = getWardPropertyName(geoJSON);
    const nameMap = {};
    
    geoJSON.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || 
                       feature.properties.Ward_Name || 
                       feature.properties.WARD_NAME ||
                       `Ward ${wardNum}`;
      
      // Handle both string and numeric ward numbers
      const numericWardNum = typeof wardNum === 'number' ? wardNum : parseInt(wardNum, 10);
      if (!isNaN(numericWardNum)) {
        nameMap[numericWardNum] = wardName;
      }
    });
    
    return nameMap;
  }, [geoJSON]);

  useEffect(() => {
    const loadForecastData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Format date for API (power outage uses explicit start date)
        const formatDateForAPI = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(forecastDay);
        selectedDate.setHours(0, 0, 0, 0);
        const dayOffset = Math.floor((selectedDate - today) / (1000 * 60 * 60 * 24));

        // Fetch forecast data based on indicator type
        let rawData;
        if (isGroundwater) {
          rawData = await fetchGroundwaterForecast({ city });
        } else if (isInformalWorkers) {
          rawData = await fetchInformalWorkersForecast({ start_date: formatDateForAPI(today), days: 7, lookback_years: 5, city });
        } else if (isConstructionLabor) {
          rawData = await fetchConstructionLaborForecast({ start_date: formatDateForAPI(today), days: 7, lookback_years: 5, city });
        } else {
          rawData = await fetchPowerOutageForecast(formatDateForAPI(today), city);
        }
        
        console.log('ForecastTableContainer - Raw data received:', {
          isGroundwater,
          hasWardPredictions: !!rawData?.ward_predictions,
          wardPredictionsLength: rawData?.ward_predictions?.length,
          hasDailySummary: !!rawData?.daily_summary,
          dailySummaryLength: rawData?.daily_summary?.length
        });
        
        // Create a map of API data by ward number for quick lookup
        const apiDataMap = {};
        if (rawData && rawData.ward_predictions) {
          rawData.ward_predictions.forEach(ward => {
            const prediction = ward.predictions && ward.predictions[dayOffset];
            apiDataMap[ward.ward_number] = prediction;
          });
        }

        // Iterate through ALL wards from GeoJSON (same as map does) to ensure consistent counting
        const wardData = [];
        if (geoJSON && geoJSON.features) {
          const wardPropertyName = getWardPropertyName(geoJSON);
          
          geoJSON.features.forEach((feature) => {
            const wardNum = feature.properties[wardPropertyName];
            let numericWardNum = null;
            if (typeof wardNum === 'number') {
              numericWardNum = wardNum;
            } else if (typeof wardNum === 'string') {
              const parsed = parseInt(wardNum, 10);
              if (!isNaN(parsed)) {
                numericWardNum = parsed;
              }
            }
            
            if (numericWardNum === null) return;
            
            const wardName = wardNameMap[numericWardNum] || 
                            feature.properties.ward_name || 
                            feature.properties.Ward_Name || 
                            `Ward ${numericWardNum}`;
            
            const prediction = apiDataMap[numericWardNum];
            
            if (isGroundwater) {
              const threshold = getIndicatorThreshold('Groundwater stress levels');
              const dep = prediction && prediction.predicted_depletion_level_meters != null
                ? Number(prediction.predicted_depletion_level_meters)
                : null;
              const severity_level = dep != null
                ? calculateGroundwaterSeverityLevel(dep, threshold)
                : 'N/A';
              wardData.push({
                ward_number: numericWardNum,
                ward_name: wardName,
                severity_level,
                predicted_depletion_level_meters: dep,
                predicted_current_gwl_meters: prediction && prediction.predicted_current_gwl_meters != null
                  ? Number(prediction.predicted_current_gwl_meters)
                  : null,
                predicted_mean_gwl_meters: prediction && prediction.predicted_mean_gwl_meters != null
                  ? Number(prediction.predicted_mean_gwl_meters)
                  : null
              });
            } else if (isInformalWorkers) {
              // For informal workers - use predicted_severity_index
              const threshold = getIndicatorThreshold(indicatorName || 'No show rate among informal workers');
              const hasData = prediction && prediction.predicted_severity_index != null;
              const severityIndex = hasData ? (prediction.predicted_severity_index || 0) : null;
              const severity_level = hasData
                ? calculateInformalWorkersSeverityLevel(severityIndex)
                : 'N/A';
              
              wardData.push({
                ward_number: numericWardNum,
                ward_name: wardName,
                severity_level,
                predicted_severity_index: severityIndex,
                forecast_temp_c: prediction && prediction.forecast_temp_c != null
                  ? prediction.forecast_temp_c
                  : null,
                confidence: prediction && prediction.confidence ? prediction.confidence : 'N/A'
              });
            } else if (isConstructionLabor) {
              // For construction labor - use predicted_severity_index
              const threshold = getIndicatorThreshold(indicatorName || 'Lost labor hours due to heat stress');
              const hasData = prediction && prediction.predicted_severity_index != null;
              const severityIndex = hasData ? (prediction.predicted_severity_index || 0) : null;
              
              // Determine which severity calculation function to use based on indicator name
              let severity_level = 'N/A';
              if (hasData) {
                if (indicatorName === 'Lost labor hours due to heat stress') {
                  severity_level = calculateLostLaborHoursSeverityLevel(severityIndex);
                } else if (indicatorName === '% of workers without access to shade/water') {
                  severity_level = calculateNoShadeWaterSeverityLevel(severityIndex);
                } else if (indicatorName === 'Reported workplace heat illness cases') {
                  severity_level = calculateHeatIllnessSeverityLevel(severityIndex);
                } else {
                  severity_level = calculateLostLaborHoursSeverityLevel(severityIndex);
                }
              }
              
              wardData.push({
                ward_number: numericWardNum,
                ward_name: wardName,
                severity_level,
                predicted_severity_index: severityIndex,
                forecast_temp_c: prediction && prediction.forecast_temp_c != null
                  ? prediction.forecast_temp_c
                  : null,
                confidence: prediction && prediction.confidence ? prediction.confidence : 'N/A'
              });
            } else {
              // For power outage - include all wards, show N/A for missing data
              const threshold = getIndicatorThreshold('Power outages');
              const hasData = prediction && prediction.predicted_severity_index != null;
              const severityIndex = hasData ? (prediction.predicted_severity_index || 0) : null;
              const severity_level = hasData
                ? calculateSeverityLevel(severityIndex, threshold)
                : 'N/A';
              
              wardData.push({
                ward_number: numericWardNum,
                ward_name: wardName,
                severity_level,
                predicted_severity_index: severityIndex,
                predicted_duration_minutes: prediction && prediction.predicted_duration_minutes != null
                  ? prediction.predicted_duration_minutes
                  : null,
                predicted_consumers_affected: prediction && prediction.predicted_consumers_affected != null
                  ? prediction.predicted_consumers_affected
                  : null,
                forecast_temp_c: prediction && prediction.forecast_temp_c != null
                  ? prediction.forecast_temp_c
                  : null,
                confidence: prediction && prediction.confidence ? prediction.confidence : 'N/A'
              });
            }
          });
        }

        setForecastData(wardData);
      } catch (err) {
        console.error('Error loading forecast data:', err);
        setError(err.message || 'Failed to load forecast data');
      } finally {
        setIsLoading(false);
      }
    };

    if (forecastDay) {
      loadForecastData();
    }
  }, [city, forecastDay, geoJSON, wardNameMap, isGroundwater, isInformalWorkers, isConstructionLabor, indicatorName, getIndicatorThreshold]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!filteredForecastData) return [];

    const sorted = [...filteredForecastData].sort((a, b) => {
      const key = sortConfig.key;
      let aValue = a[key];
      let bValue = b[key];

      // Severity: use legend order HIGH > MEDIUM > LOW > N/A (for both groundwater and power outage)
      if (key === 'severity_level') {
        const aOrder = SEVERITY_ORDER[aValue] ?? SEVERITY_ORDER['N/A'];
        const bOrder = SEVERITY_ORDER[bValue] ?? SEVERITY_ORDER['N/A'];
        if (aOrder !== bOrder) {
          return sortConfig.direction === 'desc' ? bOrder - aOrder : aOrder - bOrder;
        }
        return 0;
      }

      const aNull = aValue == null;
      const bNull = bValue == null;
      if (aNull && bNull) return 0;
      if (aNull) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bNull) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  const SortIcon = ({ colKey }) => {
    const icon = getSortIcon(colKey);
    if (!icon) return null;
    return (
      <span style={{ marginLeft: '0.25rem', fontSize: '0.7rem', color: '#6c757d' }}>
        {icon}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minHeight: '400px',
        color: '#666'
      }}>
        Loading forecast data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minHeight: '400px',
        color: '#d32f2f',
        padding: '1rem'
      }}>
        Error: {error}
      </div>
    );
  }

  const sortedData = getSortedData();
  const totalWardCount = forecastData?.length ?? 0;
  const filteredWardCount = sortedData.length;
  const selectedDateStr = new Date(forecastDay).toLocaleDateString('en-US', { 
    weekday: 'short', 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });

  return (
    <div className="forecast-table-container" style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: '500px',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{
        padding: '1rem 1.25rem',
        borderBottom: '2px solid #e0e0e0',
        background: '#f8f9fa'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          fontWeight: 600,
          color: '#2c3e50'
        }}>
          {isGroundwater 
            ? 'Forecasted Groundwater Data' 
            : isInformalWorkers 
            ? 'Forecasted Informal Workers Data'
            : isConstructionLabor
            ? 'Forecasted Construction Labor Data'
            : 'Forecasted Power Outage Data'} - {selectedDateStr}
        </h3>
        <p style={{
          margin: '0.25rem 0 0 0',
          fontSize: '0.8rem',
          color: '#666'
        }}>
          {searchQuery.trim()
            ? `${filteredWardCount} of ${totalWardCount} wards`
            : `${wardsWithForecastData} wards with forecast data`}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search ward name or number…"
            aria-label="Search ward name or number"
            style={{
              flex: 1,
              minWidth: 0,
              padding: '0.5rem 0.6rem',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
          {searchQuery.trim() && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: '#495057'
              }}
              title="Clear search"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table Container */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        position: 'relative',
        width: '100%',
        maxWidth: '100%'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.85rem',
          tableLayout: 'auto',
          minWidth: 0
        }}>
          {/* Fixed Header */}
          <thead style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <tr>
              <th
                onClick={() => handleSort('ward_number')}
                style={{
                  padding: '0.75rem 0.5rem',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  fontWeight: 600,
                  color: '#2c3e50',
                  borderBottom: '2px solid #dee2e6',
                  cursor: 'pointer',
                  userSelect: 'none',
                  background: '#fff',
                  whiteSpace: 'normal',
                  fontSize: '0.8rem'
                }}
              >
                S.No.<SortIcon colKey="ward_number" />
              </th>
              <th
                onClick={() => handleSort('ward_name')}
                style={{
                  padding: '0.75rem 0.5rem',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  fontWeight: 600,
                  color: '#2c3e50',
                  borderBottom: '2px solid #dee2e6',
                  cursor: 'pointer',
                  userSelect: 'none',
                  background: '#fff',
                  whiteSpace: 'normal',
                  fontSize: '0.8rem'
                }}
              >
                Ward Name (Ward No.)<SortIcon colKey="ward_name" />
              </th>
              {isGroundwater ? (
                <>
                  <th
                    onClick={() => handleSort('severity_level')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Severity<SortIcon colKey="severity_level" />
                  </th>
                  <th
                    onClick={() => handleSort('predicted_depletion_level_meters')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Predicted Depletion (m)<SortIcon colKey="predicted_depletion_level_meters" />
                  </th>
                  <th
                    onClick={() => handleSort('predicted_current_gwl_meters')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Predicted Current GWL (m)<SortIcon colKey="predicted_current_gwl_meters" />
                  </th>
                  <th
                    onClick={() => handleSort('predicted_mean_gwl_meters')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Predicted Mean GWL (m)<SortIcon colKey="predicted_mean_gwl_meters" />
                  </th>
                </>
              ) : isInformalWorkers || isConstructionLabor ? (
                <>
                  <th
                    onClick={() => handleSort('severity_level')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Severity<SortIcon colKey="severity_level" />
                  </th>
                  <th
                    onClick={() => handleSort('predicted_severity_index')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Predicted Severity Index<SortIcon colKey="predicted_severity_index" />
                  </th>
                  <th
                    onClick={() => handleSort('forecast_temp_c')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Forecast Temperature (°C)<SortIcon colKey="forecast_temp_c" />
                  </th>
                  <th
                    onClick={() => handleSort('confidence')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Confidence<SortIcon colKey="confidence" />
                  </th>
                </>
              ) : (
                <>
                  <th
                    onClick={() => handleSort('severity_level')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Severity<SortIcon colKey="severity_level" />
                  </th>
                  <th
                    onClick={() => handleSort('predicted_duration_minutes')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Predicted Outage Duration (min)<SortIcon colKey="predicted_duration_minutes" />
                  </th>
                  <th
                    onClick={() => handleSort('predicted_consumers_affected')}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      color: '#2c3e50',
                      borderBottom: '2px solid #dee2e6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: '#fff',
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                      lineHeight: '1.2'
                    }}
                  >
                    Predicted Consumers Affected<SortIcon colKey="predicted_consumers_affected" />
                  </th>
                </>
              )}
            </tr>
          </thead>

          {/* Scrollable Body */}
          <tbody>
            {sortedData.map((row, index) => {
              const severityLevel = row.severity_level || 'N/A';
              const legendStyle = severityLevel && severityLevel !== 'N/A'
                ? SEVERITY_COLORS[severityLevel] || SEVERITY_COLORS['N/A']
                : null;
              const rowBg = legendStyle ? legendStyle.bg : (index % 2 === 0 ? '#fff' : '#f9f9f9');
              const clickableWardInfo = wardInfoByNumber[Number(row.ward_number)];
              return (
              <tr
                key={row.ward_number}
                style={{
                  background: rowBg,
                  borderLeft: legendStyle ? `4px solid ${legendStyle.border}` : undefined,
                  cursor: clickableWardInfo ? 'pointer' : 'default',
                  transition: 'background 0.2s'
                }}
                title={clickableWardInfo ? 'Click to center map on this ward' : undefined}
                onClick={() => {
                  if (!clickableWardInfo) return;
                  setSelectedWard(clickableWardInfo);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = legendStyle ? legendStyle.bg : '#f0f7ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = rowBg;
                }}
              >
                <td style={{
                  padding: '0.75rem 0.5rem',
                  borderBottom: '1px solid #e9ecef',
                  color: '#2c3e50',
                  fontSize: '0.8rem'
                }}>
                  {index + 1}
                </td>
                <td style={{
                  padding: '0.75rem 0.5rem',
                  borderBottom: '1px solid #e9ecef',
                  color: '#495057',
                  fontSize: '0.8rem'
                }}>
                  {row.ward_name} ({row.ward_number})
                </td>
                {isGroundwater ? (
                  <>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'center',
                      fontWeight: 600,
                      color: legendStyle ? legendStyle.border : '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {severityLevel}
                    </td>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'right',
                      color: '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {row.predicted_depletion_level_meters != null ? row.predicted_depletion_level_meters.toFixed(2) : 'N/A'}
                    </td>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'right',
                      color: '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {row.predicted_current_gwl_meters != null ? row.predicted_current_gwl_meters.toFixed(2) : 'N/A'}
                    </td>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'right',
                      color: '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {row.predicted_mean_gwl_meters != null ? row.predicted_mean_gwl_meters.toFixed(2) : 'N/A'}
                    </td>
                  </>
                ) : isInformalWorkers || isConstructionLabor ? (
                  <>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'center',
                      fontWeight: 600,
                      color: legendStyle ? legendStyle.border : '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {severityLevel}
                    </td>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'right',
                      color: '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {row.predicted_severity_index != null ? row.predicted_severity_index.toFixed(2) : 'N/A'}
                    </td>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'right',
                      color: '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {row.forecast_temp_c != null ? row.forecast_temp_c.toFixed(1) : 'N/A'}
                    </td>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'center',
                      color: '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {row.confidence || 'N/A'}
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'center',
                      fontWeight: 600,
                      color: legendStyle ? legendStyle.border : '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {severityLevel}
                    </td>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'right',
                      color: '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {row.predicted_duration_minutes != null ? row.predicted_duration_minutes.toFixed(0) : 'N/A'}
                    </td>
                    <td style={{
                      padding: '0.75rem 0.5rem',
                      borderBottom: '1px solid #e9ecef',
                      textAlign: 'right',
                      color: '#495057',
                      fontSize: '0.8rem'
                    }}>
                      {row.predicted_consumers_affected != null ? row.predicted_consumers_affected.toLocaleString() : 'N/A'}
                    </td>
                  </>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ForecastTableContainer;
